import { z } from 'zod';
import { INVENTORY_UNITS, QUOTE_STATUSES, SIZE_CLASSES } from '../constants.js';
import { cursorQuerySchema, paginated, uuidSchema } from './common.js';
import { lineCoverageSnapshotSchema } from './pricing.js';
import { operatingHoursSchema, preferredDateSchema, preferredTimeSchema } from '../booking.js';
const finishSchema = z.string().trim().max(20);
// §29 additive fields shared by both the create-input and read-back line item
// schemas — the new engine's richer input/output, all optional/nullable so
// old callers and pre-§29 saved quotes are unaffected (0012's ALTER TABLE
// added these as nullable/defaulted columns on quote_line_items).
const pricingEngineLineItemFields = {
    serviceKind: z.enum(['wrap', 'ppf', 'tint', 'ceramic', 'print']).nullable().optional(),
    vehicleCatalogTrimId: uuidSchema.nullable().optional(),
    workspaceVehicleId: uuidSchema.nullable().optional(),
    // Canonical selection snapshot (see lineCoverageSnapshotSchema): everything
    // needed to re-derive the engine input from IDs/keys alone. Nullable/optional
    // so pre-§29 lines (no serviceKind) and old saved quotes are unaffected.
    coverage: lineCoverageSnapshotSchema.nullable().optional(),
    disassemblyLevel: z.enum(['L0', 'L1', 'L2', 'L3']).nullable().optional(),
    doorJambs: z.boolean().optional(),
    interiorTrimPieces: z.number().int().optional(),
    estimatedMinutes: z.number().int().nullable().optional(),
    // §34.3 - cure/hold time this line needs after work finishes (PPF/ceramic
    // only; wrap/tint leave it null). Persisted so app.convert_quote_to_job can
    // roll it up onto jobs.cure_hold_minutes (see D-34-13).
    cureHoldMinutes: z.number().int().nullable().optional(),
    filmFtX10: z.number().int().nullable().optional(),
    materialCostCents: z.number().int().nullable().optional(),
    laborCostCents: z.number().int().nullable().optional(),
    breakdown: z.record(z.string(), z.unknown()).optional(),
    profileVersionId: uuidSchema.nullable().optional(),
    // Coverage-based (PPF/tint/ceramic) lines only - which vehicle size class
    // priced this line's sqft/laborHours (§ coverage_size_multipliers). Absent
    // for §29 wrap lines and old pre-this-feature saved lines; resolveLineItems
    // treats a missing value as 'sedan' (the 1.0x baseline).
    sizeClass: z.enum(SIZE_CLASSES).nullable().optional(),
    // Material consumption/reservation snapshot (inventory item + qty/unit
    // reserved for this line, and its cost at reservation time) - null for
    // pre-this-feature saved lines and lines with no linked inventory item.
    // Named reservedMaterialCostCents (not materialCostCents) because that
    // name is already taken above by the §29 engine's line material cost.
    materialInventoryItemId: uuidSchema.nullable().optional(),
    materialQty: z.number().min(0).nullable().optional(),
    materialUnit: z.enum(INVENTORY_UNITS).nullable().optional(),
    reservedMaterialCostCents: z.number().int().min(0).nullable().optional(),
};
export const quoteLineItemInputSchema = z.object({
    serviceTypeId: uuidSchema,
    coverageId: uuidSchema,
    materialId: uuidSchema,
    color: z.string().trim().max(40).default(''),
    finish: finishSchema.default(''),
    notes: z.string().trim().max(1000).default(''),
    sort: z.number().int().default(0),
    ...pricingEngineLineItemFields,
});
export const quoteLineItemSchema = z.object({
    id: uuidSchema.optional(),
    serviceTypeId: uuidSchema.nullable(),
    materialId: uuidSchema.nullable(),
    serviceName: z.string(),
    coverageName: z.string(),
    materialBrand: z.string(),
    materialSeries: z.string(),
    color: z.string(),
    finish: z.string(),
    sqft: z.number().nullable(),
    laborHours: z.number(),
    materialRateCents: z.number(),
    laborRateCents: z.number(),
    brandMultiplier: z.number(),
    lineTotalCents: z.number(),
    notes: z.string(),
    sort: z.number(),
    ...pricingEngineLineItemFields,
});
export const saveQuoteSchema = z.object({
    customerId: uuidSchema,
    // Nullable since 0074: a draft (e.g. converted from a widget lead) starts
    // without a vehicle; sending the quote still requires one.
    vehicleId: uuidSchema.nullable(),
    leadId: uuidSchema.optional(),
    lineItems: z.array(quoteLineItemSchema).default([]),
    customerNotes: z.string().trim().max(2000).default(''),
    internalNotes: z.string().trim().max(2000).default(''),
    depositPct: z.number().min(0).max(100).optional(),
    discountName: z.string().trim().max(60).default(''),
    discountPct: z.number().min(0).max(100).default(0),
    taxEnabled: z.boolean().default(true),
    depositCustomCents: z.number().int().min(0).nullable().optional(),
    priceSource: z.enum(['ai', 'manual']).default('manual'),
    aiTotalCents: z.number().int().nullable().optional(),
    pricingSnapshot: z.record(z.string(), z.unknown()).default({}),
    clipBreakageDisclaimerAckAt: z.string().nullable().optional(),
    adhesionRisk: z.boolean().optional(),
    adhesionWaiverAckAt: z.string().nullable().optional(),
    fleetDiscountApplied: z.boolean().optional(),
    rushFlag: z.boolean().optional(),
    // §34.2 customer preferred booking date/time - shape only here (date
    // without time allowed, time without date rejected) so this object stays a
    // plain ZodObject and updateQuoteSchema's .partial() below keeps working;
    // the cross-field rule is enforced by preferredBookingSchema server-side
    // (see server/src/modules/quotes/booking.ts).
    preferredDate: preferredDateSchema.nullable().optional(),
    preferredTime: preferredTimeSchema.nullable().optional(),
    // Per-quote lead time for the customer's preferred booking date: 0 means no
    // restriction (any date from today), absent means "leave as is" on update
    // and the 14-day default on create (column default, 0073).
    bookingMinLeadDays: z.number().int().min(0).max(90).optional(),
    locationId: uuidSchema.optional(),
});
export const quoteListQuerySchema = cursorQuerySchema.extend({
    status: z.enum(QUOTE_STATUSES).optional(),
    customerId: uuidSchema.optional(),
    include: z.string().optional(),
    locationId: uuidSchema.optional(),
});
export const quoteListItemSchema = z.object({
    id: uuidSchema,
    number: z.number(),
    status: z.enum(QUOTE_STATUSES),
    customerName: z.string(),
    vehicleLabel: z.string(),
    totalCents: z.number(),
    depositCents: z.number(),
    sentAt: z.string().nullable(),
    updatedAt: z.string(),
    locationId: uuidSchema,
});
export const quoteListResponseSchema = paginated(quoteListItemSchema).and(z.object({ counts: z.record(z.string(), z.number()).optional() }));
export const quoteDetailSchema = z.object({
    id: uuidSchema,
    number: z.number(),
    status: z.enum(QUOTE_STATUSES),
    customerId: uuidSchema,
    customerName: z.string(),
    vehicleId: uuidSchema.nullable(),
    vehicleLabel: z.string(),
    currency: z.string(),
    subtotalCents: z.number(),
    taxCents: z.number(),
    totalCents: z.number(),
    depositPct: z.number(),
    depositCents: z.number(),
    discountName: z.string(),
    discountPct: z.number(),
    discountCents: z.number(),
    taxEnabled: z.boolean(),
    priceSource: z.enum(['ai', 'manual']),
    customerNotes: z.string(),
    // Written by the customer on the public quote page (brand preference,
    // questions) when accepting or paying; read-only for the shop.
    customerRequestNote: z.string(),
    internalNotes: z.string(),
    validUntil: z.string().nullable(),
    sentAt: z.string().nullable(),
    viewedAt: z.string().nullable(),
    depositPaidAt: z.string().nullable(),
    convertedJobId: uuidSchema.nullable(),
    lineItems: z.array(quoteLineItemSchema),
    shareUrl: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    clipBreakageDisclaimerAckAt: z.string().nullable(),
    adhesionRisk: z.boolean(),
    adhesionWaiverAckAt: z.string().nullable(),
    fleetDiscountApplied: z.boolean(),
    rushFlag: z.boolean(),
    // §34.2 - the saved preference plus the workspace's selectable window/hours
    // data, so the client can render valid options without duplicating the
    // shop-timezone/operating-hours logic that computed them server-side.
    preferredDate: z.string().nullable(),
    preferredTime: z.number().nullable(),
    windowStart: z.string(),
    operatingHours: operatingHoursSchema,
    bookingMinLeadDays: z.number(),
    locationId: uuidSchema,
    // Opaque in-progress Step-3 service-builder state (the fields the shop had
    // entered but not yet added as a line), stored on the draft so leaving and
    // reopening restores the builder exactly as left. Never recomputed/validated
    // server-side (unlike lineItems) - it round-trips through pricing_snapshot.
    draftServiceState: z.record(z.string(), z.unknown()).nullable(),
});
export const sendQuoteSchema = z.object({
    channels: z.array(z.enum(['email', 'whatsapp', 'sms', 'link'])).min(1),
    message: z.string().trim().max(1000).optional(),
    // Shop attests the customer agreed to receive texts about this quote.
    smsConsent: z.boolean().optional(),
});
export const sendQuoteResponseSchema = z.object({
    shareUrl: z.string(),
    waLink: z.string().nullable(),
    smsText: z.string().nullable(),
    smsError: z.string().nullable(),
});
export const declineQuoteSchema = z.object({ reason: z.string().trim().max(300).optional() });
export const voidQuoteSchema = z.object({}).optional();
export const duplicateQuoteResponseSchema = z.object({ id: uuidSchema, number: z.number() });
export const priceOverrideSchema = z.object({
    totalCents: z.number().int().min(0),
});
// re-exported for the wizard's naming convenience
export const createQuoteSchema = saveQuoteSchema;
export const updateQuoteSchema = saveQuoteSchema.partial({ customerId: true, vehicleId: true });
//# sourceMappingURL=quotes.js.map